import React from 'react';
import classNames from 'classnames/bind';
import styles from './SelectCustomTypeForm.module.scss';
import usePopupHandler from '@/hooks/custom/usePopupHandler';
import { CustomTravelEstimateType } from '@pkgtour-workspace/common/dist/constants/estimate';
import { CustomTravelEstimatePopupStep, CustomTravelParamType } from '@/components/CustomTravel/EstimatePopup';
import ButtonField from '@/components/CustomTravel/ServiceFormFields/ButtonField';
import ConditionalField from '@/components/CustomTravel/ServiceFormFields/ConditionalField';
import ServiceForm from '@/components/CustomTravel/ServiceForm';
import { CustomTravelFormState } from '@/atoms/customTravelFormState';
import { DimmedContent, DimmedFooter } from '@/components/CustomTravel/DimmedPopup';

const cx = classNames.bind(styles);

interface SelectCustomTypeFormProps {
  externalValues?: { estimateType?: CustomTravelEstimateType };
}

const SelectCustomTypeForm: React.FC<SelectCustomTypeFormProps> = ({ externalValues }) => {
  const { handleClose, onSubmitNextStep } = usePopupHandler();

  const handleSubmit = (value: CustomTravelFormState) => {
    if (value.estimateType) {
      const isEstimateTypeAdvisor = value.estimateType === CustomTravelEstimateType.ADVISOR;
      onSubmitNextStep(
        { estimateType: value.estimateType },
        CustomTravelEstimatePopupStep.SelectGroupType,
        isEstimateTypeAdvisor ? { type: CustomTravelParamType.ADVISOR_TYPE } : undefined,
      );
    }
  };

  return (
    <ServiceForm onSubmit={handleSubmit} externalValues={externalValues}>
      <div
        className={cx('SelectCustomType')}
        data-event-nlog="imp"
        data-event-area-code="customtravel.popup.customtype"
      >
        <DimmedContent>
          <span className={cx('title')}>
            단체여행 신청 방법 선택
            <b>
              여행지, 일정, 인원이
              <br />
              정해지셨나요?
            </b>
          </span>
          <div className={cx('buttons')} role="radiogroup">
            <ButtonField
              name={'estimateType'}
              value={CustomTravelEstimateType.SELF}
              className={cx('button', 'self')}
              nlogAreaCode="cus.self"
            >
              <div className={cx('text')}>
                <b>네! 정해졌어요!</b>
                여행지, 일정, 인원, 예산을 입력하고 요청을 정확하게 작성할 수 있어요.
              </div>
            </ButtonField>
            <ButtonField
              name={'estimateType'}
              value={CustomTravelEstimateType.ADVISOR}
              className={cx('button', 'advisor')}
              nlogAreaCode="cus.adv"
            >
              <div className={cx('text')}>
                <b>아니요! 상담하면서 정할게요!</b>
                여행 어드바이저와 상담을 통해 단체여행 신청을 도와드려요.
              </div>
            </ButtonField>
          </div>
        </DimmedContent>
        <DimmedFooter>
          <div className={cx('progress')}>
            <button
              type="button"
              className={cx('prev')}
              onClick={handleClose}
              data-event-nlog="click"
              data-event-area-code="cus.typebackpage"
            >
              이전
            </button>
            <ConditionalField<CustomTravelEstimateType> targetFieldName="estimateType">
              {(estimateType) => (
                <button
                  type="submit"
                  className={cx('next')}
                  disabled={!estimateType}
                  data-event-nlog="click"
                  data-event-area-code="cus.selfgrouptypenextpage"
                >
                  다음
                </button>
              )}
            </ConditionalField>
          </div>
        </DimmedFooter>
      </div>
    </ServiceForm>
  );
};

export default SelectCustomTypeForm;
